import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function RatePlans() {
  return (
    <>
      <p>
        A rate plan is a set of commercial terms: how a customer's AWS cost becomes their price. You build plans on{" "}
        <ScreenLink href="/rate-plans">Rate plans</ScreenLink> and give one to a customer on the customer's{" "}
        <strong>Billing</strong> tab. One plan can serve many customers. Billing admins edit; billing viewers can read.
      </p>

      <h2 id="plan-lifecycle">Draft, active, retired</h2>
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>What you can do</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Draft</td>
            <td>Edit anything. Not yet usable by a customer.</td>
          </tr>
          <tr>
            <td>Active</td>
            <td>
              Can be assigned. <strong>Cannot be changed</strong>, so a customer's terms for a month never move under them.
            </td>
          </tr>
          <tr>
            <td>Retired</td>
            <td>No longer offered for new assignments. Months already priced by it keep it.</td>
          </tr>
        </tbody>
      </table>
      <p>
        To change a customer's terms, <strong>Clone</strong> their plan, edit the copy, activate it, and assign it from a
        future month. A closed month's terms never change. If two people edit the same draft, the second save is refused;
        reload and apply your change again.
      </p>

      <h2 id="rates-and-tiers">Markup, rates and tiers</h2>
      <p>A plan combines any of these:</p>
      <ul>
        <li>
          <strong>Markup (%)</strong>, plan-wide: price = cost × (1 + markup). 30 % turns $1,000 of cost into $1,300.
        </li>
        <li>
          <strong>Rates</strong>: rows for a model and token type (either can be “any”), each with its own markup % or a
          fixed price per million tokens. A fixed price ignores cost. The most specific row wins: model and type, then
          model, then type, then “any, any”, then the plan-wide markup.
        </li>
        <li>
          <strong>Volume tiers</strong>: a discount % from a monthly spend upwards. Tiers are graduated, like tax bands, and
          start at $0.
        </li>
        <li>
          <strong>Monthly minimum</strong>: usage is topped up to it. <strong>Platform fee</strong>: a flat amount each
          month.
        </li>
        <li>
          <strong>Cost basis</strong>: whether the markup applies to AWS list price or the actual AWS bill. See{" "}
          <HelpLink to="pricing-and-markup" section="cost-basis" />.
        </li>
      </ul>
      <p>
        Tier example: 0 % from $0, 10 % from $1, 20 % from $5. A month with $7 of usage price gets $4 × 10 % + $2 × 20 % =
        $0.80 off, so $6.20.
      </p>
      <p>Every invoice is worked out in this order, and its lines appear in the same order:</p>
      <ol>
        <li>Usage, priced per model, token type and day by the rate that applies.</li>
        <li>Upward token corrections.</li>
        <li>Volume discount.</li>
        <li>Trial credits.</li>
        <li>Write-offs.</li>
        <li>Monthly minimum top-up (skipped in a month with an active trial).</li>
        <li>Platform fee.</li>
        <li>Credits, downward corrections, and late usage from earlier months.</li>
        <li>A $0 floor: any excess credit is carried to the customer's next billed invoice.</li>
      </ol>
      <p>
        The plan editor has a worked example: pick a customer, and it prices that customer's last closed month under
        your draft, so you can check the numbers before activating.
      </p>

      <h2 id="assigning-a-plan">Assigning a plan</h2>
      <ul>
        <li>
          A plan is assigned to a customer <strong>from a month</strong>, on the customer's Billing tab. It applies from
          that month until a later assignment takes over.
        </li>
        <li>Only an active plan can be assigned, and a month holds one assignment.</li>
        <li>
          A month that is closed, or has begun, is locked: you cannot assign into a closed month, and you cannot delete an
          assignment whose month has started.
        </li>
        <li>
          A <strong>default plan</strong> prices billed months of customers that have no assignment of their own. It never
          makes a customer billed by itself.
        </li>
      </ul>

      <h2 id="billing-mode">Billed or not, and who pays AWS</h2>
      <p>Two settings on the customer's Billing tab, both changed from the start of a month:</p>
      <table>
        <thead>
          <tr>
            <th>Setting</th>
            <th>Choices</th>
            <th>Means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Billing</td>
            <td>Billed / Not billed</td>
            <td>
              Not billed is “proxy only”: usage is metered and reported, but no invoice or statement is made, and it is
              left out of revenue. Every customer starts not billed.
            </td>
          </tr>
          <tr>
            <td>Who pays AWS</td>
            <td>AllCode / The customer</td>
            <td>
              Whose AWS bill the Bedrock usage lands on. Only AllCode-paid usage counts as AllCode's AWS cost. It does not
              change what the customer is invoiced.
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        Switching a customer to billed needs a plan assigned to the customer itself from that month; you can pick it in the
        same step. The default plan does not count.
      </p>
      <Callout>
        Trials, adjustments and plan assignments are refused for a not-billed month. Switch the month to billed first.
      </Callout>

      <h2 id="cost-visibility">Cost visibility</h2>
      <p>
        Decides whether the <strong>customer's own admins</strong> see AWS cost in their reports: list cost shown, or
        hidden. It applies at once, to past and current months alike.
        AllCode staff always see cost. The customer's statement never shows cost either way. A customer on a simple markup
        can work its margin out from the statement; that is expected, since the markup is part of its terms.
      </p>

      <Related to={["pricing-and-markup", "trials-and-credits", "usage-adjustments", "closing-a-month"]} />
    </>
  );
}
